import React, { useContext, useEffect, useState } from "react";
import { MdClose, MdLightbulb } from "react-icons/md";
import { Link, Navigate } from "react-router-dom";

import { badges, Question, QuestionId } from "@common";
import { Box, Divider, Grid, Modal, Typography } from "@mui/material";
import { blue } from "@mui/material/colors";
import {
  TeacherModeQuizModule,
  // teacherModeQuizModules,
  // extraTeacherModeQuizModules,
} from "@natklok/config/teacherModeQuizModules";
import getApproxQuizTime from "@natklok/util/getApproxQuizTime";

import { HTMLRenderer, StyledButton } from "../components";
import WobblyTypography from "../components/WobblyTypography";
import { gold, purple } from "../config";
import { DiscussionSlideData } from "../config/discussionSlideData";
import { bungeeFont } from "../config/theme";
import { LangContext } from "../contexts/langContext";
import { MultiplayerSettingsContext } from "../contexts/multiplayerSettingsContext";
import { WebsocketContext } from "../contexts/websocketContext";
import RenderQuestion from "../features/RenderQuestion";
import useSocket from "../hooks/useWebsocket";
import { QuizSlideDataSequence } from "../interfaces/question.interface";
import {
  getNOfMatchingQuestions,
  getQuestionFromId,
  // getQuestionFromId,
} from "../util/questionUtils";
import { generateQuizSlideDataSequenceFromTeacherModeQuiz } from "../util/slideDataUtils";
import useTranslatePageText from "@natklok/hooks/useTranslatePageText";
import useDefaultSlideDatas from "@natklok/hooks/useDefaultSlideDatas";
import { useQuestions } from "@natklok/hooks/useQuestions";
import useTranslateTranslationData from "@natklok/hooks/useTranslateTranslationData";
import { useTeacherQuizModules } from "@natklok/hooks/useTeacherQuizModules";

const HostOnboarding = () => {
  const [redirect, setRedirect] = useState(false);
  const { age } = useContext(MultiplayerSettingsContext);
  const { translatePageText, translateTranslationData } = useContext(LangContext);
  const { roomCode } = useContext(WebsocketContext);
  const { wsSetQuiz, wsHostRoom } = useSocket();
  const { data: questionsData } = useQuestions();
  const [nOfMatchingQuestions, setNOfMatchingQuestions] = useState(
    // TODO COMPLETE: Add questions
    getNOfMatchingQuestions({ profiles: ["general"], age, questions: questionsData ?? [] })
  );
  useEffect(() => {
    const questions = questionsData ?? [];
    setNOfMatchingQuestions(getNOfMatchingQuestions({ profiles: ["general"], age, questions }));
  }, [age, questionsData]);
  const defaultSlideDatas = useDefaultSlideDatas();

  const [listOfModules] = useState(() =>
    Object.keys(badges)
      .map((badgeId) => {
        return {
          id: badgeId,
          title: translatePageText(`progressionDisplay.${badgeId}`),
          desc: translatePageText(`progressionDisplay.groupDesc.${badgeId}`),
          nOfQuestions: nOfMatchingQuestions.badgeIdCounts[badgeId],
        };
      })
      .filter((module) => module.nOfQuestions > 0)
  );
  const [matchingModules, setMatchingModules] = useState(listOfModules);
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    const matches = [];
    for (const module of listOfModules) {
      if (
        module.title
          .replace(/ /g, "")
          .toLowerCase()
          .includes(searchQuery.replace(/ /g, "").toLowerCase()) ||
        module.desc
          .replace(/ /g, "")
          .toLowerCase()
          .includes(searchQuery.replace(/ /g, "").toLowerCase())
      ) {
        matches.push(module);
      }
    }
    setMatchingModules(matches);
  }, [searchQuery, listOfModules]);

  const onSelectQuiz = (quiz: TeacherModeQuizModule) => {
    const quizSlideDataSequence: QuizSlideDataSequence =
      generateQuizSlideDataSequenceFromTeacherModeQuiz({
        teacherModeQuizModule: quiz,
        defaultSlideDatas,
        translateTranslationData,
      });

    if (roomCode === undefined) {
      wsHostRoom();
    }

    wsSetQuiz(quizSlideDataSequence);
    setRedirect(true);
  };

  if (redirect) return <Navigate to="/multiplayer/skapavanterum" />;

  return (
    <Box display="flex" flexDirection="column" minHeight="90vh" justifyContent="space-between">
      <ModeQuestion />
      <Box bgcolor={purple[600]} width="100%">
        <QuizList onSelect={onSelectQuiz} />
      </Box>
    </Box>
  );
};

const ModeQuestion: React.FC = () => {
  const translatePageText = useTranslatePageText();
  return (
    <Box width="100%" display="flex">
      <Box width="100%" margin={2}>
        <Box marginBottom={4} marginTop={6} maxWidth="700px">
          <WobblyTypography variant="h4" component="h1">
            {translatePageText("hostOnboarding.chooseQuiz")}
          </WobblyTypography>
          <Typography mt={1} color={blue[900]}>
            {translatePageText("hostOnboarding.recommendedAudience")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const QuizList: React.FC<{ onSelect: (quiz: TeacherModeQuizModule) => void }> = ({ onSelect }) => {
  const translatePageText = useTranslatePageText();
  // TODO: Add teacherModeQuizModules AND extraTeacherModeQuizModules
  const { data: teacherModeQuizModulesData } = useTeacherQuizModules();
  const teacherModeQuizModules = teacherModeQuizModulesData ?? [];
  // const extraTeacherModeQuizModules: any = [];
  return (
    <Box padding={4} paddingTop={2} paddingBottom={8} bgcolor={purple[500]} minHeight="80vh">
      <Grid container spacing={2}>
        {teacherModeQuizModules
          .filter((quiz) => quiz.isExtraMaterial == false)
          .map((quiz: TeacherModeQuizModule) => (
            <Grid item xs={12} sm={6} md={4} lg={4} xl={3} marginTop={6}>
              <QuizCard quiz={quiz} onSelect={() => onSelect(quiz)} />
            </Grid>
          ))}
      </Grid>

      <Box color="white" marginTop="60px">
        <Typography variant="body2">{translatePageText("general.extraquiz")}</Typography>
        <Box bgcolor="white" height="1px" width="100%" />
      </Box>
      <Grid container spacing={2}>
        {teacherModeQuizModules
          .filter((quiz) => quiz.isExtraMaterial)
          .map((quiz: TeacherModeQuizModule) => (
            <Grid item xs={12} sm={6} md={4} lg={4} xl={3} marginTop={6}>
              <QuizCard quiz={quiz} onSelect={() => onSelect(quiz)} />
            </Grid>
          ))}
      </Grid>
    </Box>
  );
};

const QuizCard: React.FC<{
  quiz: TeacherModeQuizModule;
  onSelect: () => void;
}> = ({ quiz, onSelect }) => {
  const [moreInfoOpen, setMoreInfoOpen] = useState(false);

  const translateTranslationData = useTranslateTranslationData();
  const translatePageText = useTranslatePageText();

  const showMoreInfo = () => setMoreInfoOpen(true);
  const closeMoreInfo = () => setMoreInfoOpen(false);

  return (
    <>
      <Box
        padding={2}
        bgcolor={purple[600]}
        borderRadius={2}
        color="white"
        height="100%"
        position="relative"
      >
        <Box
          position="absolute"
          top="0"
          right="10px"
          style={{ transform: "translateY(-30%)" }}
          display="flex"
        >
          <Box color={gold[600]} bgcolor="white" borderRadius={100} paddingX={1} marginX={0.5}>
            <Typography fontFamily={bungeeFont} variant="caption">
              {getApproxQuizTime(
                quiz.questionIdsOrVariants.length + quiz.discussionSlides.length,
                true
              )}{" "}
              {translatePageText("forTeachers.minutes")}
            </Typography>
          </Box>
        </Box>
        <Typography variant="h6" marginBottom={0.5}>
          {translateTranslationData(quiz.title)}
        </Typography>
        <Typography style={{ marginBottom: 40 }}>{translateTranslationData(quiz.desc)}</Typography>
        <div
          style={{
            display: "flex",
            width: "100%",
            position: "absolute",
            bottom: -30,
            left: 0,
            gap: 16,
          }}
        >
          {/* <StyledButton
            style={{ flex: 1 }}
            variant="secondary"
            label={translatePageText("hostOnboarding.seeLayout")}
            onClick={showMoreInfo}
          /> */}
          <StyledButton
            style={{ flex: 1, maxWidth: 220 }}
            label={translatePageText("hostOnboarding.chooseThisQuiz")}
            onClick={onSelect}
          />
        </div>
      </Box>
      <MoreInfoModal
        quiz={quiz}
        open={moreInfoOpen}
        handleClose={closeMoreInfo}
        onSelect={onSelect}
      />
    </>
  );
};

const MoreInfoModal: React.FC<{
  open: boolean;
  quiz: TeacherModeQuizModule;
  handleClose: () => void;
  onSelect: () => void;
}> = ({ open, quiz, handleClose, onSelect }) => {
  const translatePageText = useTranslatePageText();
  const translateTranslationData = useTranslateTranslationData();
  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="modal-preview-question">
      <Box
        position="absolute"
        top="50%"
        left="50%"
        zIndex={2}
        sx={{ transform: "translate(-50%, -50%)" }}
        padding={2}
      >
        <Box
          sx={{
            bgcolor: "white",
            boxShadow: 24,
            borderRadius: 3,
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 40,
              height: 40,
              transform: "translate(30%, -30%)",
              bgcolor: "primary.main",
              color: "white",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 100,
              cursor: "pointer",
              zIndex: 100,
            }}
            onClick={() => handleClose()}
          >
            <MdClose fontSize="1.4rem" />
          </Box>

          <Box
            style={{
              height: "80vh",
              width: "80vw",
              maxWidth: 800,
              overflow: "scroll",
              padding: "16px 10%",
            }}
          >
            <Typography marginTop={6} marginBottom={1} color={blue[300]}>
              {translatePageText("hostOnboarding.layout")}
            </Typography>
            <Typography variant="h4">{translateTranslationData(quiz.title)}</Typography>
            <Typography mb={4} marginTop={1} color={blue[600]}>
              {translateTranslationData(quiz.desc)}
            </Typography>
            <Box
              bgcolor={blue[100]}
              padding={2}
              marginTop={1}
              borderLeft="4px solid"
              borderRadius={1}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <Typography marginTop={1} fontWeight="bold">
                  {translatePageText("quizCard.tips")}
                </Typography>
                <MdLightbulb style={{ marginLeft: 4, fontSize: 18, marginTop: 6 }} />
              </div>

              <Typography color={blue[600]} mb={1}>
                {translatePageText("quizCard.tryQuiz")}
              </Typography>
              <StyledButton size="small" variant="secondary" onClick={onSelect}>
                {translatePageText("quizCard.tryItOut")}
              </StyledButton>
            </Box>
            <Divider style={{ margin: "16px 0" }} />
            <Templates quiz={quiz} onSelect={onSelect} />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

const Templates: React.FC<{ quiz: TeacherModeQuizModule; onSelect: () => void }> = ({
  quiz,
  onSelect,
}) => {
  const translateTranslationData = useTranslateTranslationData();
  const translatePageText = useTranslatePageText();

  const questions = useQuestions().data ?? [];

  return (
    <>
      <>
        <Typography variant="h6">Quizet startas </Typography>
        <Typography>{translatePageText("quizCard.whenAllPlayersJoin")}</Typography>
        <Divider style={{ margin: "16px 0" }} />
      </>
      <>
        <Typography variant="h6" marginTop={4} marginBottom={2}>
          {translateTranslationData(quiz.introSlideTitle)} · Föreläsning
        </Typography>
        <Box
          style={{
            overflow: "hidden",
            height: 300,
            background: purple[700],
            borderRadius: 16,
            padding: 16,
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            marginBottom: 8,
            color: "white",
          }}
        >
          <HTMLRenderer>{translateTranslationData(quiz.introSlideDesc)}</HTMLRenderer>
        </Box>

        <Divider style={{ margin: "16px 0" }} />

        {quiz.questionIdsOrVariants.map((questionId: QuestionId, index: number) => {
          const question = getQuestionFromId({ questionId, questions });

          if (!question) return null;

          return (
            <>
              <Typography variant="h6" marginTop={4} marginBottom={2}>
                Fråga {index + 1}
              </Typography>
              <Box
                style={{
                  overflow: "hidden",
                  height: 420,
                  background: purple[700],
                  borderRadius: 16,
                  padding: 16,
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  color: "white",
                  marginBottom: 8,
                }}
              >
                <div style={{ transform: "scale(0.5)" }}>
                  <RenderQuestion question={question} />
                </div>
              </Box>
              <Link target="_blank" rel="noopener noreferrer" to={`/question/${question.id.sv}`}>
                <StyledButton label="Prova frågan själv i en ny flik" />
              </Link>

              <Divider style={{ margin: "16px 0" }} />
            </>
          );
        })}

        {quiz.discussionSlides.map((discussionSlide, index: number) => {
          if (discussionSlide === undefined) return null;

          return (
            <>
              <Typography variant="h6" marginTop={4} marginBottom={2}>
                Diskussions fråga {index + 1}
              </Typography>

              <Typography>{discussionSlide.title}</Typography>

              <Divider style={{ margin: "16px 0" }} />
            </>
          );
        })}
      </>

      <>
        <Typography variant="h6">Quizet avslutas </Typography>
        <Typography>
          Här syns statistik på hur det har gått för spelarna och vilka frågor som var svårast. Här
          kan även ett till quiz startas enkelt.
        </Typography>

        <Divider style={{ margin: "16px 0" }} />
      </>
      <Box textAlign="center" bgcolor={blue[100]} p={6} borderRadius={4}>
        <Typography variant="h6" mb={1}>
          Prova quizzet på egen hand
        </Typography>
        <StyledButton variant="secondary" onClick={onSelect}>
          Prova här
        </StyledButton>
      </Box>
    </>
  );
};

export default HostOnboarding;
